import { useEffect, useState } from 'react';

import type { InterstateOnChangeCallback, InterstateOnChangeEvent } from '@interstate/components/InterstateEvents';
import type { DatePickerEventValue } from '@interstate/components/DatePicker/Types/datePickerTypes';
import type { SelectInputOption } from '@interstate/components/SelectInput';
import type { TextInputValue } from '@interstate/components/TextInput';
import type { ComboBoxOptions } from '@interstate/components/ComboBox';

import { LienholderAddressKeys } from './constants';
import type { LienholderFormData, LienholderStateData } from '../../../types';
import { formatNumberAndCents } from '@makemydeal/dr-common-utils';
import { convertToNumber } from '../../../utils/formatUtils';
import { isBlurEvent, StateCodes } from '@makemydeal/dr-shared-ui-utils';

export type TradeInErrorState = Record<string, boolean>;

export const getStateCodesSelectInputValues = (): (SelectInputOption & { label: string })[] | ComboBoxOptions =>
    StateCodes.map((stateCode) => ({
        value: stateCode,
        label: stateCode
    }));

export const checkLienholderInfoAvailability = ({
    lienholder,
    lienholderAccountNumber,
    lienholderAddress: { address, city, postalCode, stateProvince },
    goodThroughDate,
    perDiemInterest
}: LienholderStateData) => {
    return (
        !!lienholder ||
        !!lienholderAccountNumber ||
        !!address ||
        !!city ||
        !!postalCode ||
        !!stateProvince ||
        !!goodThroughDate ||
        !!perDiemInterest
    );
};

export const getLienholderFormData = (sourceLienholderData: LienholderStateData): LienholderFormData => ({
    lienholder: sourceLienholderData.lienholder,
    lienholderAccountNumber: sourceLienholderData.lienholderAccountNumber,
    lienholderPhoneNumber: sourceLienholderData.lienholderPhoneNumber,
    lienholderAddress: sourceLienholderData.lienholderAddress,
    payoffDueDate: sourceLienholderData.goodThroughDate ? new Date(sourceLienholderData.goodThroughDate) : null,
    perDiem: sourceLienholderData.perDiemInterest
});

export const extractValueFromDatePickerEvent = (event: InterstateOnChangeEvent<DatePickerEventValue>): Date | null => {
    const stringValue = event.target.value.dateValue;

    return stringValue ? new Date(stringValue) : null;
};

export const getRequiredErrorMessage = (obj: Record<string, boolean>, key: string) => {
    return obj[key] ? 'Required' : undefined;
};

// This workaround is needed until interstate fixes the issue with SelectInput and NumericInput
export const isLienholderAddressInvalid = (field: string, e: InterstateOnChangeEvent<TextInputValue>) => {
    const {
        isValid,
        target: { value }
    } = e;

    if (!isValid && field !== 'stateProvince') {
        return true;
    }

    return field === 'postalCode' && value && typeof value === 'string' && value.length !== 5;
};

// This workaround is needed until interstate fixes the issue with DatePicker
export const isLienholderDataInvalid = (field: string, value?: number | Date | null) => {
    return field === 'payoffDueDate' && value && value < new Date();
};

type onChangeResult = {
    value: string;
    handleChange: InterstateOnChangeCallback<string>;
    handleBlur: InterstateOnChangeCallback<string>;
};

export const useHandlePerDiemChange = (
    initialValue: number,
    onChange: (value: 'perDiem') => InterstateOnChangeCallback<any>,
    onBlur: (e: InterstateOnChangeEvent<string>) => void
): onChangeResult => {
    const [value, setValue] = useState(initialValue);
    const [textValue, setTextValue] = useState(formatNumberAndCents(initialValue));

    const handleChange = (e: InterstateOnChangeEvent<string>) => {
        if (isBlurEvent(e)) {
            return;
        }
        setTextValue(e.target.value);
    };
    const handleBlur = (e: InterstateOnChangeEvent<string>) => {
        setTextValue(formatNumberAndCents(value));
        onBlur(e);
    };

    useEffect(() => {
        const newValue = convertToNumber(textValue);
        setValue(newValue);
        if (newValue !== value) {
            onChange('perDiem')({ target: { value: newValue }, isValid: true });
        }
    }, [onChange, textValue, value]);

    useEffect(() => {
        if (initialValue === value) return;
        setValue(initialValue);
        setTextValue(formatNumberAndCents(initialValue));
    }, [initialValue, value]);

    return { value: textValue, handleChange, handleBlur };
};

// Grid configurations for different layouts
export const tradeInContainerGridPropsConfigWithBreakdownClose = { xs: 4, md: 4, lg: 2, xl: 1.5, sm: 4 };
export const tradeInContainerGridPropsConfigWithBreakdownOpen = { xs: 4, md: 4, lg: 4, xl: 2, sm: 4 };
export const tradeInComponentLargeFieldsConfigWithBreakdownOpen = { sm: 8, xs: 8, md: 8, lg: 8, xl: 4 };
export const tradeInComponentLargeFieldsConfigWithBreakdownClose = { sm: 8, xs: 8, md: 8, lg: 4, xl: 3 };

// Compact UI specific configurations
export const tradeInContainerGridPropsCompactUI = tradeInContainerGridPropsConfigWithBreakdownOpen;

const MOBILE_TWO_COLUMN_WIDTH = 6;

export const tradeInContainerLargeFieldsProps = (
    isBreakDownOpen: boolean,
    isLargeScreen?: boolean,
    isDealCentralMultiScenario?: boolean,
    isCompactUI?: boolean
) => {
    if (isCompactUI) {
        return tradeInContainerGridPropsCompactUI;
    }
    if (isDealCentralMultiScenario) {
        return {
            ...tradeInComponentLargeFieldsConfigWithBreakdownClose,
            lg: tradeInComponentLargeFieldsConfigWithBreakdownClose.xs,
            xl: tradeInComponentLargeFieldsConfigWithBreakdownClose.xs
        };
    }
    if (!isLargeScreen) {
        const baseConfig = isBreakDownOpen
            ? { ...tradeInComponentLargeFieldsConfigWithBreakdownOpen, xl: tradeInComponentLargeFieldsConfigWithBreakdownOpen.lg }
            : {
                  ...tradeInComponentLargeFieldsConfigWithBreakdownClose,
                  xl: tradeInComponentLargeFieldsConfigWithBreakdownClose.lg
              };

        return { ...baseConfig, xs: MOBILE_TWO_COLUMN_WIDTH };
    }
    return isBreakDownOpen
        ? tradeInComponentLargeFieldsConfigWithBreakdownOpen
        : tradeInComponentLargeFieldsConfigWithBreakdownClose;
};

export const tradeInContainerGridProps = (
    isBreakDownOpen: boolean,
    isLargeScreen?: boolean,
    isDealCentralMultiScenario?: boolean,
    isCompactUI?: boolean
) => {
    if (isCompactUI) {
        return tradeInContainerGridPropsCompactUI;
    }
    if (isDealCentralMultiScenario) {
        return {
            ...tradeInContainerGridPropsConfigWithBreakdownClose,
            lg: tradeInContainerGridPropsConfigWithBreakdownClose.xs,
            xl: tradeInContainerGridPropsConfigWithBreakdownClose.xs
        };
    }
    if (!isLargeScreen) {
        const baseConfig = isBreakDownOpen
            ? { ...tradeInContainerGridPropsConfigWithBreakdownOpen, xl: tradeInContainerGridPropsConfigWithBreakdownOpen.lg }
            : { ...tradeInContainerGridPropsConfigWithBreakdownClose, xl: tradeInContainerGridPropsConfigWithBreakdownClose.lg };

        return { ...baseConfig, xs: MOBILE_TWO_COLUMN_WIDTH };
    }
    return isBreakDownOpen ? tradeInContainerGridPropsConfigWithBreakdownOpen : tradeInContainerGridPropsConfigWithBreakdownClose;
};

export const defaultLienholderAddressErrors = Object.fromEntries(LienholderAddressKeys.map((key) => [key, false]));

export const defaultLienholderFormErrors = {
    postalCodeInValid: false,
    lienholderPhoneNumber: false,
    lienholder: false,
    addressInValid: false,
    cityInValid: false,
    stateProvinceInValid: false
};

export const updateTradeInErrorState = (
    currentErrorState: TradeInErrorState[],
    tradeInIndex: number,
    fieldsToUpdate: TradeInErrorState
): TradeInErrorState[] => {
    const updatedErrorState = [...currentErrorState];

    updatedErrorState[tradeInIndex] = {
        ...(updatedErrorState[tradeInIndex] || {}),
        ...fieldsToUpdate
    };

    return updatedErrorState;
};
